// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useOverlayBadge from "@insite/client-framework/Common/Hooks/useOverlayBadge";
import useWidgetBadges from "@insite/client-framework/Common/Hooks/useWidgetBadges";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
import { ProductContext, ProductContextModel } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import updateProductInfo from "@insite/client-framework/Store/Components/ProductInfoList/Handlers/UpdateProductInfo";
import { getProductInfoFromList } from "@insite/client-framework/Store/Components/ProductInfoList/ProductInfoListSelectors";
import sendProductRecommendationTracking from "@insite/client-framework/Store/Components/ProductRecommendations/Handlers/SendProductRecommendationTracking";
import translate from "@insite/client-framework/Translate";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import ProductAddToListLink, {
    ProductAddToListLinkStyles,
} from "@insite/content-library/Components/ProductAddToListLink";
import { ProductAvailabilityStyles } from "@insite/content-library/Components/ProductAvailability";
import ProductBrand, { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import ProductContextAvailability from "@insite/content-library/Components/ProductContextAvailability";
import ProductDescription, { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import ProductImage, { ProductImageStyles } from "@insite/content-library/Components/ProductImage";
import ProductImageBadges from "@insite/content-library/Components/ProductImageBadges";
import ProductPartNumbers, { ProductPartNumbersStyles } from "@insite/content-library/Components/ProductPartNumbers";
import ProductPrice from "@insite/content-library/Components/ProductPrice";
import ProductQuantityBreakPricing, {
    ProductQuantityBreakPricingStyles,
} from "@insite/content-library/Components/ProductQuantityBreakPricing";
import ProductTextBadges from "@insite/content-library/Components/ProductTextBadges";
import Badge from "@insite/mobius/Badge";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import { SelectPresentationProps } from "@insite/mobius/Select";
import { TextFieldProps } from "@insite/mobius/TextField";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import ProductContextPreviousPurchase from "@plumberssupply/App/Components/Product/ProductContextPreviousPurchase";
import ProductGridLayout, { ProductGridLayoutStyle } from "@plumberssupply/App/Components/Product/ProductGridLayout";
import ProductScratchAndDentBadge from "@plumberssupply/App/Components/Product/ProductScratchAndDentBadge";
import RoundToBox, { RoundToBoxStyles } from "@plumberssupply/App/Components/Product/RoundToBox";
import { CustomAppState } from "@plumberssupply/App/Store/Reducers";
import { getXngCommon } from "@plumberssupply/App/Store/Selectors/CommonSelectors";
import { getRoundToBox, getShowRoundToBox } from "@plumberssupply/App/Store/Selectors/CustomProductDetailsSelectors";
import ProductAddToCartButton from "@plumberssupply/Overrides/Components/ProductAddToCartButton";
import ProductQuantityOrdered from "@plumberssupply/Overrides/Components/ProductQuantityOrdered";
import ProductUnitOfMeasureSelect from "@plumberssupply/Overrides/Components/ProductUnitOfMeasureSelect";
import { xScreen } from "@plumberssupply/XDL/Utilities";
import React, { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: CustomAppState, ownProps: OwnProps) => {
    const id = state.xSystemBuilder.condenserProductId;
    return {
        productInfo:
            state.xSystemBuilder.relatedProducts?.[id || ""]?.productsInfo?.[ownProps?.product?.productNumber || ""],
        isTabletScreen: getXngCommon(state).isTabletScreen,
    };
};

interface OwnProps {
    carouselId: string;
    product: ProductModel;
    showImage: boolean;
    showBrand: boolean;
    showTitle: boolean;
    showPartNumbers: boolean;
    showPrice: boolean;
    showAddToCart: boolean;
    showAddToList: boolean;
    showImageBadges?: boolean;
    maximumNumberOfImageBadges?: number;
    showTextBadges?: boolean;
    maximumNumberOfTextBadges?: number;
    extendedStyles?: RelatedProductCartStyles;
    showInventory?: boolean;
}

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

export interface RelatedProductCartStyles {
    productImageStyles?: ProductImageStyles;
    productBrandStyles?: ProductBrandStyles;
    productDescriptionStyles?: ProductDescriptionStyles;
    productPartNumbersStyles?: ProductPartNumbersStyles;
    productPriceWrapper?: InjectableCss;
    productUnitOfMeasureSelectStyles?: SelectPresentationProps;
    productQuantityBreakPricingStyles?: ProductQuantityBreakPricingStyles;
    productQuantityOrderedStyles?: TextFieldProps;
    productAddToCartButtonStyles?: ButtonPresentationProps;
    productAddToListLinkStyles?: ProductAddToListLinkStyles;
    badgeAreaWrapper?: InjectableCss;
    badgeTextWrapper?: InjectableCss;
    badgeImageWrapper?: InjectableCss;
    badgeText?: TypographyPresentationProps;
    badgeImageProps?: JSX.IntrinsicElements["img"];
    productGridLayoutStyle?: ProductGridLayoutStyle;
    productAvailabilityStyles?: ProductAvailabilityStyles;
    roundToBox?: RoundToBoxStyles;
    topSection?: InjectableCss;
    bottomSection?: InjectableCss;
    actionsSection?: InjectableCss;
}

export const relatedProductCartStyles: RelatedProductCartStyles = {};

const RelatedProductCart = ({
    carouselId,
    product,
    showImage,
    showBrand,
    showTitle,
    showPartNumbers,
    showPrice,
    showInventory,
    showAddToCart,
    showAddToList,
    showImageBadges,
    maximumNumberOfImageBadges,
    showTextBadges,
    maximumNumberOfTextBadges,
    extendedStyles,
    productInfo,
    isTabletScreen,
}: Props) => {
    const [styles] = useState(() => mergeToNew(relatedProductCartStyles, extendedStyles));
    const [qty, setQty] = useState(productInfo?.qtyOrdered || 1);
    const [unitOfMeasure, setUnitOfMeasure] = useState(productInfo?.unitOfMeasure || "");

    useEffect(() => {
        productInfo?.unitOfMeasure && setUnitOfMeasure(productInfo.unitOfMeasure);
    }, [productInfo?.unitOfMeasure]);

    const updateQuantity = (qtyOrdered: number) => {
        setQty(qtyOrdered);
    };

    const updateUnitOfMeasure = (unitOfMeasure: string) => {
        setUnitOfMeasure(unitOfMeasure);
    };

    const productContext = {
        product: {
            ...product,
        },
        productInfo: {
            ...productInfo,
            qtyOrdered: qty,
            unitOfMeasure,
        },
        onUnitOfMeasureChanged: updateUnitOfMeasure,
        onQtyOrderedChanged: updateQuantity,
    } as ProductContextModel;

    const {
        showOverlayBadge,
        overlayBadge,
        overlayBadgeWrapperStyles,
        badgeAreaWrapperRef,
        overlayBadgeWrapperClickHandler,
    } = useOverlayBadge({
        productContext,
        showTextBadge: showTextBadges,
        showImageBadge: showImageBadges,
        styles,
    });
    const { imageBadges, textBadges } = useWidgetBadges({ productContext, showTextBadges, showImageBadges });

    const [roundToBox, setRoundToBox] = useState(getRoundToBox(product?.properties));

    if (!product || !productInfo) {
        return null;
    }

    return (
        <>
            <ProductContext.Provider value={productContext}>
                <ProductGridLayout
                    id={`product_${product.id}`}
                    data-test-selector="product-carousel-item"
                    extendedStyles={styles.productGridLayoutStyle}
                >
                    <ProductGridLayout.Image>
                        {showImage && !showOverlayBadge && (
                            <ProductImage product={productContext} extendedStyles={styles.productImageStyles} />
                        )}
                        {showImage && showOverlayBadge && overlayBadge && (
                            <StyledWrapper
                                {...styles.badgeAreaWrapper}
                                ref={badgeAreaWrapperRef}
                                data-test-selector="productCarouselBadge"
                            >
                                <ProductImage extendedStyles={styles.productImageStyles} product={productContext} />
                                <StyledWrapper {...overlayBadgeWrapperStyles} onClick={overlayBadgeWrapperClickHandler}>
                                    <Badge
                                        type={overlayBadge.badgeType}
                                        value={
                                            overlayBadge.badgeType === "Image"
                                                ? overlayBadge.largeImageBadgePath
                                                : overlayBadge.displayText
                                        }
                                        badgeTextColor={overlayBadge.textColorHexCode}
                                        badgeBackgroundColor={overlayBadge.badgeColorHexCode}
                                        badgeTextRounding={overlayBadge.badgeStyle === "Round" ? 20 : 0}
                                        imageAltText={
                                            overlayBadge.imageAltText ? overlayBadge.imageAltText : overlayBadge.name
                                        }
                                        textBadgeCss={styles.badgeText}
                                        imageBadgeProps={styles.badgeImageProps}
                                    />
                                </StyledWrapper>
                            </StyledWrapper>
                        )}
                        <ProductContextPreviousPurchase />
                    </ProductGridLayout.Image>
                    <StyledWrapper {...styles.topSection}>
                        <ProductGridLayout.Info>
                            {showBrand && product.brand && (
                                <ProductBrand brand={product.brand} extendedStyles={styles.productBrandStyles} />
                            )}
                            {showTitle && (
                                <ProductDescription
                                    product={productContext}
                                    extendedStyles={styles.productDescriptionStyles}
                                />
                            )}
                            {showPartNumbers && !isTabletScreen && (
                                <ProductPartNumbers
                                    productNumber={product.productNumber}
                                    customerProductNumber={product.customerProductNumber}
                                    manufacturerItem={product.manufacturerItem}
                                    showCustomerName={false}
                                    showManufacturerItem={true}
                                    extendedStyles={styles.productPartNumbersStyles}
                                />
                            )}
                        </ProductGridLayout.Info>
                        <ProductGridLayout.AvailabilitySection>
                            {showInventory && (
                                <ProductContextAvailability extendedStyles={styles.productAvailabilityStyles} />
                            )}
                        </ProductGridLayout.AvailabilitySection>
                        {/* <ProductContextOriginally /> */}
                        {showTextBadges && textBadges && (
                            <ProductTextBadges badgeList={textBadges} maxBadges={maximumNumberOfTextBadges ?? 4} />
                        )}
                        {showImageBadges && imageBadges && (
                            <ProductImageBadges badgeList={imageBadges} maxBadges={maximumNumberOfImageBadges ?? 4} />
                        )}
                    </StyledWrapper>
                    <StyledWrapper {...styles.bottomSection}>
                        <ProductGridLayout.PriceSection>
                            {showPrice && (
                                <StyledWrapper {...styles.productPriceWrapper}>
                                    <VisuallyHidden
                                        as="label"
                                        htmlFor={`${carouselId}-uom`}
                                        id={`${carouselId}-uom-label`}
                                    >
                                        {translate("U/M")}
                                    </VisuallyHidden>
                                    <ProductPrice
                                        showLabel={false}
                                        product={productContext}
                                        showUnitOfMeasure={product && product.unitOfMeasures?.length === 1}
                                    />
                                    {product.unitOfMeasures && (
                                        <ProductUnitOfMeasureSelect
                                            labelOverride=""
                                            extendedStyles={styles.productUnitOfMeasureSelectStyles}
                                            id={`${carouselId}-uom`}
                                        />
                                    )}
                                    <ProductQuantityBreakPricing
                                        extendedStyles={styles.productQuantityBreakPricingStyles}
                                    />
                                    {getShowRoundToBox(product.properties) && (
                                        <RoundToBox
                                            isChecked={roundToBox}
                                            sizeVariant="small"
                                            toggleHandler={setRoundToBox}
                                            extendedStyles={styles.roundToBox}
                                        />
                                    )}
                                </StyledWrapper>
                            )}
                            <ProductScratchAndDentBadge />
                        </ProductGridLayout.PriceSection>
                        <StyledWrapper {...styles.actionsSection}>
                            <ProductGridLayout.AddToCartSection>
                                {showAddToCart && (
                                    <>
                                        <VisuallyHidden
                                            as="label"
                                            htmlFor={`${carouselId}-qty`}
                                            id={`${carouselId}-qty-label`}
                                        >
                                            {translate("QTY")}
                                        </VisuallyHidden>
                                        <ProductQuantityOrdered
                                            labelOverride=""
                                            showActions={true}
                                            extendedStyles={styles.productQuantityOrderedStyles}
                                            id={`${carouselId}-qty`}
                                        />
                                        <ProductAddToCartButton
                                            data-test-selector="addToCartBtn"
                                            isRoundToBox={roundToBox}
                                            extendedStyles={styles.productAddToCartButtonStyles}
                                            iconOnly={isTabletScreen}
                                        />
                                    </>
                                )}
                            </ProductGridLayout.AddToCartSection>
                            <ProductGridLayout.AddToListAction>
                                {showAddToList && (
                                    <ProductAddToListLink
                                        data-test-selector="addToListLink"
                                        {...{ isRoundToBox: roundToBox }}
                                        extendedStyles={styles.productAddToListLinkStyles}
                                    />
                                )}
                            </ProductGridLayout.AddToListAction>
                        </StyledWrapper>
                    </StyledWrapper>
                </ProductGridLayout>
            </ProductContext.Provider>
        </>
    );
};

RelatedProductCart.defaultProps = {
    showInventory: true,
};

export default connect(mapStateToProps)(RelatedProductCart);
